"use client";

/*
 * /services/spectrum/ — Optical Spectrum (Spectrum as a Service).
 *
 * Every figure reads from a data module: slot widths and the optics they
 * suit from src/data/spectrum.ts, delivery footprint from the same file
 * (own DWDM line system), managed wavelength rates from pricing.ts.
 * Price, SLA and lead time are per route and are stated as such; no
 * numbers are invented for them.
 */
import { ServicePageLayout, type ServicePageData } from "@/components/ServicePageLayout";
import { useTranslation } from "@/lib/i18n";
import {
  SPECTRUM_SLOTS,
  SPECTRUM_SLOTS_LABEL,
  SPECTRUM_SLOTS_SHORT,
  SPECTRUM_POP_IDS,
  SPECTRUM_HIGH_RATE,
  formatGhz,
} from "@/data/spectrum";
import { DWDM_CAPACITY_MAX_LABEL } from "@/lib/pricing";

const POP_COUNT = SPECTRUM_POP_IDS.length;
const [SLOT_A, SLOT_B] = SPECTRUM_SLOTS;

const ucIcon = (d: string) => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const heroIcon = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12c2.5-6 4.5-6 6.75 0s4.25 6 6.75 0 4.25-6 6-0" />
  </svg>
);

const ICON_AI = ucIcon("M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z");
const ICON_CLOUD = ucIcon("M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z");
const ICON_CARRIER = ucIcon("M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0");

const COMMON = {
  accentColor: "#00a882",
  icon: heroIcon,
  ctaService: "spectrum",
  bilingual: true,
  hideConfigure: true,
  coveragePopIds: SPECTRUM_POP_IDS,
} as const;

function dataEn(): ServicePageData {
  return {
    ...COMMON,
    title: "Optical Spectrum",
    subtitle: "Spectrum as a Service",
    heroDescription: `Your own ${SPECTRUM_SLOTS_LABEL.en} of dedicated spectrum on our DWDM line system. You run your transponders and choose the modulation and the capacity; we run and monitor the line. Available across our DWDM network from ${POP_COUNT} PoPs, priced per route. ${SPECTRUM_HIGH_RATE} is available today in the ${formatGhz(SLOT_A.ghz, "en")} slot, after we measure the line parameters on your route.`,
    ctaText: "Request a spectrum quote",
    coverageText: `Delivered on our own DWDM line system, from ${POP_COUNT} PoPs. Dubai is reached through a long-haul partner; spectrum on that leg is on request.`,
    ctaLeadText: "Price, SLA and lead time are set per route in the quote.",
    specs: [
      { label: "Slot width", value: SPECTRUM_SLOTS_SHORT.en },
      { label: `Optics for ${formatGhz(SLOT_A.ghz, "en")}`, value: SLOT_A.fits.en },
      { label: `Optics for ${formatGhz(SLOT_B.ghz, "en")}`, value: "Next generation" },
      { label: "Transponders", value: "Yours" },
      { label: "Grid", value: "Flex grid" },
      { label: "Delivery", value: `${POP_COUNT} PoPs` },
      { label: "Fiber Standard", value: "G.652D" },
      { label: "Pricing", value: "On request" },
    ],
    features: [
      {
        title: "Your optics, your capacity",
        description:
          "You choose the transponder, the modulation and the line rate. The capacity you get out of the slot is decided by your equipment, not by a product tier.",
      },
      {
        title: "Upgrade without a new order",
        description:
          "When a new generation of coherent optics arrives, you swap the transponders at both ends inside the same slot. No new circuit, no new contract.",
      },
      {
        title: `${formatGhz(SLOT_A.ghz, "en")} and ${formatGhz(SLOT_B.ghz, "en")} slots`,
        description: `${formatGhz(SLOT_A.ghz, "en")} fits ${SLOT_A.fits.en} coherent optics. ${formatGhz(SLOT_B.ghz, "en")} leaves room for ${SLOT_B.fits.en} optics. Actual reach and rate depend on your optics and on the route.`,
      },
      {
        title: `${SPECTRUM_HIGH_RATE} after line measurements`,
        description: `Before we confirm ${SPECTRUM_HIGH_RATE} on a route, we measure the line parameters on that specific route and share the results, the launch-power targets and the optical budget with you. You know what your optics will see before you order.`,
      },
      {
        title: "Reserved and isolated",
        description:
          "The slot is reserved for you on every span of the route. No other channel shares it, and your traffic never leaves your own optics.",
      },
      {
        title: "Line monitored 24/7",
        description: `Our NOC monitors the amplifiers and the line around the clock. If you would rather not run optics, take a managed wavelength of up to ${DWDM_CAPACITY_MAX_LABEL} instead.`,
      },
    ],
    useCases: [
      {
        title: "AI cluster interconnect",
        description:
          "Move training data and checkpoints between AI sites at the rate of the newest coherent optics, and upgrade capacity on your own schedule.",
        icon: ICON_AI,
      },
      {
        title: "Cloud and content backbones",
        description:
          "Run your own long-haul optics between regions while we keep the line system running.",
        icon: ICON_CLOUD,
      },
      {
        title: "Carriers",
        description:
          "Extend your optical network across Southeast Europe and the Caucasus without building a line system of your own.",
        icon: ICON_CARRIER,
      },
    ],
  };
}

function dataBg(): ServicePageData {
  return {
    ...COMMON,
    title: "Оптичен спектър",
    subtitle: "Спектър като услуга",
    heroDescription: `Ваш собствен отделен спектър от ${SPECTRUM_SLOTS_LABEL.bg} по нашата DWDM линия. Вие пускате своите транспондери и избирате модулацията и капацитета, а ние поддържаме и наблюдаваме линията. Наличен в цялата ни DWDM мрежа, от ${POP_COUNT} точки на присъствие, с цена според трасето. ${SPECTRUM_HIGH_RATE} днес се предлага в слота от ${formatGhz(SLOT_A.ghz, "bg")}, след като измерим параметрите на линията по вашето трасе.`,
    ctaText: "Поискайте оферта за спектър",
    coverageText: `Доставя се по собствената ни DWDM линия, от ${POP_COUNT} точки на присъствие. До Дубай стигаме чрез партньор и спектър по този участък е по запитване.`,
    ctaLeadText: "Цената, SLA и срокът за изграждане се определят за всяко трасе в офертата.",
    specs: [
      { label: "Ширина на слота", value: SPECTRUM_SLOTS_SHORT.bg },
      { label: `Оптика за ${formatGhz(SLOT_A.ghz, "bg")}`, value: SLOT_A.fits.bg },
      { label: `Оптика за ${formatGhz(SLOT_B.ghz, "bg")}`, value: "Следващо поколение" },
      { label: "Транспондери", value: "Ваши" },
      { label: "Решетка", value: "Гъвкава" },
      { label: "Доставка", value: `${POP_COUNT} точки` },
      { label: "Стандарт на влакното", value: "G.652D" },
      { label: "Цена", value: "По запитване" },
    ],
    features: [
      {
        title: "Ваша оптика, ваш капацитет",
        description:
          "Вие избирате транспондера, модулацията и скоростта. Колко капацитет ще извадите от слота зависи от оборудването ви, а не от продуктов пакет.",
      },
      {
        title: "Надграждане без нова поръчка",
        description:
          "Когато излезе ново поколение кохерентна оптика, сменяте транспондерите в двата края в същия слот. Без нова верига и без нов договор.",
      },
      {
        title: `Слотове от ${formatGhz(SLOT_A.ghz, "bg")} и ${formatGhz(SLOT_B.ghz, "bg")}`,
        description: `В ${formatGhz(SLOT_A.ghz, "bg")} се побира кохерентна оптика ${SLOT_A.fits.bg}. ${formatGhz(SLOT_B.ghz, "bg")} дава място за оптика ${SLOT_B.fits.bg}. Реалният обхват и скорост зависят от оптиката ви и от трасето.`,
      },
      {
        title: `${SPECTRUM_HIGH_RATE} след измерване на линията`,
        description: `Преди да потвърдим ${SPECTRUM_HIGH_RATE} по дадено трасе, измерваме параметрите на линията по конкретното трасе и ви даваме резултатите, целевата входна мощност и оптичния бюджет. Знаете какво ще получи вашата оптика, преди да поръчате.`,
      },
      {
        title: "Запазен и изолиран",
        description:
          "Слотът е запазен за вас по всеки участък от трасето. Никой друг канал не го ползва и трафикът ви не излиза от вашата оптика.",
      },
      {
        title: "Линията се наблюдава 24/7",
        description: `NOC екипът ни следи усилвателите и линията денонощно. Ако не искате да поддържате собствена оптика, вземете управлявана вълна до ${DWDM_CAPACITY_MAX_LABEL}.`,
      },
    ],
    useCases: [
      {
        title: "Връзка между AI клъстери",
        description:
          "Пренасяйте данни за обучение и контролни точки между AI площадки със скоростта на най-новата кохерентна оптика и увеличавайте капацитета, когато прецените.",
        icon: ICON_AI,
      },
      {
        title: "Облачни и контент мрежи",
        description:
          "Пуснете собствена оптика за дълги разстояния между регионите, а ние поддържаме линията.",
        icon: ICON_CLOUD,
      },
      {
        title: "Оператори",
        description:
          "Разширете оптичната си мрежа в Югоизточна Европа и Кавказ, без да изграждате собствена линия.",
        icon: ICON_CARRIER,
      },
    ],
  };
}

export function SpectrumClient() {
  const { language } = useTranslation();
  return <ServicePageLayout data={language === "bg" ? dataBg() : dataEn()} />;
}
